import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import RollCallInterface from './RollCallInterface';
import AttendanceSummary from './AttendanceSummary';
import './AttendancePage.css';

const AttendancePage = ({ onBack, onTabChange, currentUser, canMarkAttendance = true, role = 'teacher', calculateAttendanceRate, getAttendanceStatus }) => {
  const [attendanceData, setAttendanceData] = useState({
    classesChecked: 0,
    totalStudents: 0,
    lateStudents: 0,
    absentStudents: 0
  });
  const [classAttendance, setClassAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('main'); // 'main', 'roll-call', 'summary'
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [showClassSelector, setShowClassSelector] = useState(false);
  const [availableClasses, setAvailableClasses] = useState([]);

  useEffect(() => {
    if (viewMode === 'main') {
      fetchAttendanceData();
      fetchAvailableClasses();
    }
  }, [selectedDate, viewMode]);

  const fetchAvailableClasses = async () => {
    try {
      const { data: classes, error } = await supabase
        .from('saas_classes')
        .select('class_id, name')
        .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3')
        .order('name');

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setAvailableClasses(classes || []);
    } catch (error) {
      console.error('Error in fetchAvailableClasses:', error);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      
      // Fetch real attendance data from Supabase
      const { data: attendanceRecords, error } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3')
        .eq('attendance_date', selectedDate);

      if (error) {
        console.error('Error fetching attendance data:', error);
        return;
      }

      // Process attendance data
      const processedData = processAttendanceData(attendanceRecords);
      setAttendanceData(processedData.summary);
      setClassAttendance(processedData.classAttendance);
      
    } catch (error) {
      console.error('Error in fetchAttendanceData:', error);
    } finally {
      setLoading(false);
    }
  };

  const processAttendanceData = (records) => {
    if (!records || records.length === 0) {
      return {
        summary: { classesChecked: 0, totalStudents: 0, lateStudents: 0, absentStudents: 0 },
        classAttendance: []
      };
    }

    // Group by class
    const classGroups = records.reduce((acc, record) => {
      if (!acc[record.class_id]) {
        acc[record.class_id] = [];
      }
      acc[record.class_id].push(record);
      return acc;
    }, {});

    // Calculate summary statistics
    const totalStudents = records.length;
    const lateStudents = records.filter(r => r.attendance_status === 'late').length;
    const absentStudents = records.filter(r => r.attendance_status === 'absent').length;
    const classesChecked = Object.keys(classGroups).length;

    // Process class attendance
    const classAttendance = Object.entries(classGroups).map(([classId, classRecords]) => {
      const total = classRecords.length;
      const present = classRecords.filter(r => r.attendance_status === 'present').length;
      const absent = classRecords.filter(r => r.attendance_status === 'absent').length;
      const late = classRecords.filter(r => r.attendance_status === 'late').length;
      const earlyLeave = classRecords.filter(r => r.attendance_status === 'early_leave').length;

      return {
        classId,
        className: classId, // You can enhance this by joining with saas_classes table
        totalStudents: total,
        present,
        absent,
        late,
        earlyLeave,
        status: '已點名'
      };
    });

    return {
      summary: { classesChecked, totalStudents, lateStudents, absentStudents },
      classAttendance
    };
  };

  const handleStartRollCall = () => {
    console.log('🔍 Debug: Start Roll Call button clicked');
    console.log('Available classes:', availableClasses);
    
    if (availableClasses.length === 0) {
      console.log('No available classes found');
      alert('沒有可用的班級');
      return;
    }
    
    if (availableClasses.length === 1) {
      // If only one class, use it directly
      console.log('Using single available class:', availableClasses[0]);
      setSelectedClass(availableClasses[0].class_id);
      setViewMode('roll-call');
    } else {
      // Show class selector
      console.log('Showing class selector for multiple classes');
      setShowClassSelector(true);
    }
  };

  const handleClassSelection = (classId) => {
    console.log('🔍 Debug: Class selected:', classId);
    setSelectedClass(classId);
    setShowClassSelector(false);
    setViewMode('roll-call');
  };

  const handleViewSummary = (classData) => {
    console.log('🔍 Debug: View summary for class:', classData);
    setSelectedClass(classData.classId);
    setViewMode('summary');
  };

  const handleGenerateReport = () => {
    console.log('🔍 Debug: Generate report button clicked');
    // Navigate to report generation view
    console.log('Generating attendance report...');
    alert('出勤報表功能即將推出！');
  };

  const handleAttendanceSettings = () => {
    console.log('🔍 Debug: Attendance settings button clicked');
    // Navigate to settings view
    console.log('Opening attendance settings...');
    alert('出勤設定功能即將推出！');
  };

  const handleBackToMain = () => {
    setViewMode('main');
    setSelectedClass(null);
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
  };

  if (loading && viewMode === 'main') {
    return (
      <div className="attendance-page">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  // Render different views based on viewMode
  if (viewMode === 'roll-call') {
    return (
      <RollCallInterface
        onBack={handleBackToMain}
        selectedClass={selectedClass}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
      />
    );
  }

  if (viewMode === 'summary') {
    return (
      <AttendanceSummary
        onBack={handleBackToMain}
        selectedClass={selectedClass}
        selectedDate={selectedDate}
      />
    );
  }

  // Class Selection Modal
  if (showClassSelector) {
    return (
      <div className="class-selector-modal">
        <div className="modal-content">
          <h2>選擇班級</h2>
          <p>請選擇要進行點名的班級：</p>
          <div className="class-list">
            {availableClasses.map((cls) => (
              <button
                key={cls.class_id}
                className="class-option"
                onClick={() => handleClassSelection(cls.class_id)}
              >
                {cls.name || cls.class_id}
              </button>
            ))}
          </div>
          <button 
            className="cancel-button"
            onClick={() => setShowClassSelector(false)}
          >
            取消
          </button>
        </div>
      </div>
    );
  }

  // Main attendance page view
  return (
    <div className="attendance-page">
      {/* Navigation */}
      <div className="navigation">
        <button className="back-button" onClick={onBack}>
          ← 返回課室管理
        </button>
      </div>

      {/* Title Section */}
      <div className="title-section">
        <h1 className="main-title">考勤紀錄</h1>
        <p className="subtitle">
          管理學生出勤狀況,記錄出席、缺席、遲到等情況
        </p>
      </div>

      {/* Date and Time Selector */}
      <div className="date-time-selector">
        <div className="date-selector">
          <label htmlFor="date-input">選擇日期:</label>
          <input
            type="date"
            id="date-input"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
        <div className="time-selector">
          <label htmlFor="time-input">選擇時間:</label>
          <input
            type="time"
            id="time-input"
            value={selectedTime}
            onChange={handleTimeChange}
          />
        </div>
      </div>

      {/* Key Statistics Cards */}
      <div className="stats-cards">
        <div className="stat-card green">
          <div className="stat-icon">✓</div>
          <div className="stat-content">
            <div className="stat-number">{attendanceData.classesChecked}</div>
            <div className="stat-label">已點名班級</div>
          </div>
        </div>
        
        <div className="stat-card blue">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-number">{attendanceData.totalStudents}</div>
            <div className="stat-label">總學生數</div>
          </div>
        </div>
        
        <div className="stat-card orange">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <div className="stat-number">{attendanceData.lateStudents}</div>
            <div className="stat-label">遲到學生</div>
          </div>
        </div>
        
        <div className="stat-card red">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-number">{attendanceData.absentStudents}</div>
            <div className="stat-label">缺席學生</div>
          </div>
        </div>
      </div>

      {/* Class Attendance Overview Table */}
      <div className="table-section">
        <h2 className="section-title">班級出勤概覽</h2>
        <div className="table-container">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>班別</th>
                <th>👥 總人數</th>
                <th>✅ 出席</th>
                <th>❌ 缺席</th>
                <th>⏰ 遲到</th>
                <th>🚪 早退</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {classAttendance.map((classData, index) => (
                <tr key={index}>
                  <td>{classData.className}</td>
                  <td>{classData.totalStudents}</td>
                  <td className="present">{classData.present}</td>
                  <td className="absent">{classData.absent}</td>
                  <td className="late">{classData.late}</td>
                  <td className="early-leave">{classData.earlyLeave}</td>
                  <td className="status">
                    {classData.status}
                  </td>
                  <td className="actions">
                    <button 
                      className="view-summary-btn"
                      onClick={() => handleViewSummary(classData)}
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Operations */}
      <div className="quick-operations">
        <h2 className="section-title">快速操作</h2>
        <div className="operation-cards">
          {canMarkAttendance && (
            <div className="operation-card green" onClick={handleStartRollCall}>
              <div className="operation-icon">📋</div>
              <div className="operation-content">
                <h3>開始點名</h3>
                <p>為選定的班級開始點名,記錄學生出勤狀況</p>
              </div>
            </div>
          )}
          
          <div className="operation-card blue" onClick={handleGenerateReport}>
            <div className="operation-icon">📊</div>
            <div className="operation-content">
              <h3>出勤報表</h3>
              <p>生成班級出勤統計報表,分析出勤趨勢</p>
            </div>
          </div>
          
          <div className="operation-card orange" onClick={handleAttendanceSettings}>
            <div className="operation-icon">⚙️</div>
            <div className="operation-content">
              <h3>出勤設定</h3>
              <p>設定出勤規則、遲到時間、缺席標準等</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendancePage;
